import React, { Key, useState, useEffect } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { Tag, Tooltip } from 'antd';
import { Plus, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { ColumnSelector, TableCommon, SkeletonTable, TopHeader, ButtonCommon, SelectCommon } from 'components/common';
import { Columns, QueryParams } from 'types';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from 'utils';
import { buildFullParams, parseSimpleFilter } from 'utils/filterBuilder';
import { setTableParams, TableCacheItem } from 'stores/slices/tableCacheSlice';
import { EInvoice, InvoiceRecord, InvoiceType } from 'models/EInvoice';
import { format } from 'date-fns';
import { useEInvoiceOperation } from 'hooks/useEInvoiceOperation';
import FilterEInvoice from 'components/common/Filter/FilterEInvoice';
import { useGetAllTemplateEInvoicesQuery } from 'services';
import { EInvoiceFilter } from 'types/invoice.type';

const InvoiceListPage: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const isPreviewMode = params.get('preview') === 'true';
  const [eInvoice, setEInvoice] = useState<InvoiceRecord[]>([]);
  const [isLoadingDownload, setIsLoadingDownload] = useState<boolean>(false);
  const [idFileDownload, setIdFileDownload] = useState<string>();
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<string>();
  const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(true);

  const { data: templateEInvoiceData, isLoading: isLoadingTemplate } = useGetAllTemplateEInvoicesQuery({
    query: { year: new Date().getFullYear() },
  });

  const {
    handleDownload,
    eInvoiceData,
    isLoading,
    // isFetching,
    filteredQuery,
    setFilteredQuery,
    dispatch,
    pageKey,
    tableState,
    handleApplyFilter,
    handleClearFilter,
  } = useEInvoiceOperation();

  const navigate = useNavigate();
  useEffect(() => {
    if (eInvoiceData?.data?.rows) {
      setEInvoice(eInvoiceData.data.rows);
      dispatch(
        setTableParams({
          pageKey,
          params: {
            lastFetched: Date.now(),
            search: filteredQuery.searchValue ?? '',
            page: filteredQuery.page ?? 1,
            pageSize: filteredQuery.size ?? 10,
            filter: filteredQuery.filter ?? '',
          } as unknown as Partial<TableCacheItem<unknown>>,
        }),
      );
    }
  }, [eInvoiceData]);

  useEffect(() => {
    if (!templateEInvoiceData?.data?.templates?.length) return;
    const templates = templateEInvoiceData.data.templates.filter((p) => p?.khmshDon === '2');
    if (!templates.length) return;
    // Chỉ set mặc định khi CHƯA có KHHDon
    if (!selectedInvoiceType) {
      const first = templates[0];
      setSelectedInvoiceType(first.khhDon);
      setFilteredQuery((prev) => ({
        ...prev,
        ...({ KHHDon: first.khhDon } as unknown as QueryParams),
      }));
    } else {
      setSelectedInvoiceType(selectedInvoiceType);
    }
  }, [templateEInvoiceData]);

  const handleReplace = (record: InvoiceRecord): void => {
    try {
      navigate('/invoices/electronic-invoices/create-invoice', { state: { replaceInvoice: record } });
    } catch (error) {
      console.log(error);
    }
  };

  const eInvoiceColumns: ColumnsType<InvoiceRecord> = [
    {
      title: 'SỐ HOÁ ĐƠN',
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      width: 140,
      fixed: 'left',
      ellipsis: true,
    },
    {
      title: 'TRẠNG THÁI',
      dataIndex: 'invoiceTypeText',
      key: 'invoiceTypeText',
      width: 160,
      sorter: (a, b) => (a?.invoiceTypeText || '')?.localeCompare((b?.invoiceTypeText || '') as string),
      filterSearch: false,
      render: (text, record) => {
        let color = 'default';
        const status = record.status;
        if (status === 'ISSUED') {
          color = 'success';
        } else if (status === 'PENDING') {
          color = 'warning';
        } else if (status === 'DRAFT') {
          color = 'processing';
        } else if (status === 'CANCELED' || status === 'FAILED' || status === 'REPLACED') {
          color = 'error';
        }
        return (
          <Tag color={color} style={{ margin: 0 }}>
            {text ? text.toUpperCase() : '---'}
          </Tag>
        );
      },
    },
    {
      title: 'NGÀY LẬP',
      dataIndex: 'signedAt',
      key: 'signedAt',
      width: 140,
      render: (date) => {
        if (!date) return <div>---</div>;

        return <div>{format(new Date(date), 'HH:mm, dd/MM/yyyy')}</div>;
      },
    },
    { title: 'MÃ THAM CHIẾU', dataIndex: 'referenceCode', key: 'referenceCode', width: 150, ellipsis: true },
    { title: 'TÊN KHÁCH HÀNG', dataIndex: 'buyerName', key: 'buyerName', width: 300, ellipsis: true },
    { title: 'MÃ SỐ THUẾ', dataIndex: 'buyerTaxCode', key: 'buyerTaxCode', width: 120 },
    {
      title: 'GIÁ TRỊ',
      dataIndex: 'totalAfterTax',
      key: 'totalAfterTax',
      width: 160,
      align: 'right',
      fixed: 'right',
      render: (text) => <span>{formatPrice(text)} VNĐ</span>,
    },
  ];

  const eInvoiceColumnsCheck: Columns[] = eInvoiceColumns.map((column) => ({
    key: column.key as string,
    title: column.title as string,
  }));

  const [checkedList, setCheckedList] = useState<(Key | undefined)[]>(eInvoiceColumns.map((column) => column.key));

  const getFilteredColumns = () => {
    return eInvoiceColumns.map((item) => ({
      ...item,
      hidden: !checkedList.includes(item.key),
    }));
  };

  const handlePagination = (query: QueryParams) => {
    const columnArray = ['status.code'];
    const filterQuery = buildFullParams(query, undefined, columnArray);
    setFilteredQuery((prev) => ({ ...prev, ...filterQuery }));
  };

  function handleView(record: InvoiceRecord): void {
    if (record.status === 'DRAFT') {
      navigate('/invoices/electronic-invoices/create-invoice', { state: { draftInvoice: record } });
    } else {
      navigate(`/invoices/electronic-invoices/view/${record.id}`);
    }
  }

  async function handleDownloadFile(record: EInvoice): Promise<void> {
    try {
      if (isLoadingDownload || idFileDownload) return;
      setIsLoadingDownload(true);
      setIdFileDownload(record.id);
      await handleDownload(record.id);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingDownload(false);
      setIdFileDownload('');
    }
  }

  const handleCreateInvoice = (): void => {
    navigate('/invoices/electronic-invoices/create-invoice');
  };

  // --- RENDERING ---
  return (
    <div className='flex flex-col h-full bg-[var(--textWhite)]'>
      {!isPreviewMode && (
        <TopHeader
          className='mb-0 rounded-none border-b'
          title='DANH SÁCH HOÁ ĐƠN ĐIỆN TỬ'
          subTitle='Tất cả các hoá đơn điện tử được hiển thị tại đây'
        />
      )}

      <div className='flex flex-row relative gap-4'>
        <div
          className={`flex-shrink-0 bg-white border-r border-gray-200 transition-all duration-300 ease-in-out  flex-col ${
            isFilterPanelVisible ? 'flex w-[320px] opacity-100 translate-x-0' : 'hidden w-0 opacity-0 -translate-x-10'
          }`}
        >
          <div className='flex items-center justify-between p-4 border-b border-gray-100 min-h-[56px]'>
            <span className='font-bold text-lg text-gray-800'>Bộ lọc</span>
            <button
              onClick={() => setIsFilterPanelVisible(false)}
              className='p-1.5 hover:bg-gray-100 rounded-md transition-colors text-gray-600'
              title='Thu gọn'
            >
              <CaretLeft size={20} weight='bold' />
            </button>
          </div>

          <div className='lex flex-col'>
            <FilterEInvoice
              onApplyFilter={handleApplyFilter}
              onClearFilter={() => handleClearFilter(selectedInvoiceType)}
            />
          </div>
        </div>

        <div className='flex-1 flex flex-col min-w-0 bg-[var(--textWhite)] overflow-auto '>
          <div className='flex lg:flex-row flex-col gap-2 justify-between items-center px-4 py-3 border-b border-gray-100'>
            <div className='flex items-center gap-2'>
              {!isFilterPanelVisible && (
                <Tooltip title='Mở bộ lọc'>
                  <ButtonCommon
                    icon={<CaretRight size={20} weight='bold' />}
                    color='primary'
                    size='xs'
                    radius='rounded'
                    onClick={() => setIsFilterPanelVisible(true)}
                    className='!px-2'
                  />
                </Tooltip>
              )}
              <div className='w-[300px]'>
                <SelectCommon
                  loading={isLoadingTemplate}
                  value={selectedInvoiceType}
                  placeholder='Loại hoá đơn'
                  options={templateEInvoiceData?.data?.templates
                    ?.filter((p) => p?.khmshDon === '2')
                    ?.map((p, index) => ({
                      label: (
                        <div className='flex items-center justify-between gap-2'>
                          <span className='font-medium truncate'>
                            {[p?.thDon, p?.khhDon]?.filter(Boolean)?.join(' - ')}
                          </span>
                          <span className='text-[12px] text-gray-500 flex-shrink-0'>
                            {p?.cLai}/{p?.sLuong}
                          </span>
                        </div>
                      ),
                      value: p?.khhDon,
                      key: index,
                    }))}
                  onChange={(value) => {
                    setSelectedInvoiceType(value);
                    setFilteredQuery((prev) => ({
                      ...prev,
                      ...({ khhDon: value } as unknown as QueryParams),
                    }));
                    handleApplyFilter({ KHHDon: String(value) } as unknown as EInvoiceFilter);
                  }}
                />
              </div>
            </div>

            <div className='flex gap-2 items-center flex-wrap'>
              <ColumnSelector
                allColumns={eInvoiceColumnsCheck}
                selectedColumns={checkedList}
                onChange={(selected) => setCheckedList(selected)}
              />
              <ButtonCommon
                icon={<Plus size={20} />}
                color='primary'
                onClick={handleCreateInvoice}
                size='xs'
                radius='rounded'
              >
                Thêm hoá đơn mới
              </ButtonCommon>
            </div>
          </div>

          <div className='flex-grow h-[calc(100vh-271px)] p-4'>
            {isLoading ? (
              <SkeletonTable columns={eInvoiceColumns.length} rows={10} hasHeader={true} />
            ) : (
              <TableCommon<InvoiceRecord>
                columns={getFilteredColumns()}
                dataSource={eInvoice || []}
                defaultPageSize={Number(filteredQuery.size) || 10}
                count={eInvoiceData?.data?.total || 0}
                displayView={true}
                onView={handleView}
                onEdit={() => {}}
                onDownload={handleDownloadFile}
                onReplace={handleReplace}
                isShowReplace={(record) =>
                  record.status !== 'DRAFT' &&
                  ![InvoiceType.DRAFT, InvoiceType.REPLACED, InvoiceType.CANCELLED].includes(record.invoiceType || -1)
                }
                tableProps={{
                  scroll: { x: 'max-content', y: 'calc(100vh - 280px)' },
                  size: 'middle',
                  className: 'h-full shadow-sm rounded-lg border border-gray-200',
                  loading: false,
                }}
                onPaginationChange={handlePagination}
                currentPage={Number(filteredQuery.page)}
                searchColumnDefault={parseSimpleFilter(tableState?.filter)}
                downLoadLoading={isLoadingDownload}
                idFileDownload={idFileDownload}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceListPage;
